import React from 'react';
import { motion } from 'motion/react';
import { Shield, Lock, Users, Key, Monitor, FileCheck, ShieldCheck, UserMinus } from 'lucide-react';

export default function AccessControlPolicy() {
  const lastUpdated = "April 21, 2026";

  const accessPrinciples = [
    {
      title: "Least Privilege",
      description: "Access to systems and data is granted based on the principle of least privilege, ensuring users only have the permissions necessary for their specific business function.",
      icon: Lock
    },
    {
      title: "Authorized Personnel Only",
      description: "Only authorized personnel with verified business needs are granted access to sensitive systems and datasets.",
      icon: Users
    },
    {
      title: "Enforced MFA",
      description: "Multi-factor authentication (MFA) is strictly enforced for all administrative and privileged access to minimize the risk of credential compromise.",
      icon: Key
    },
    {
      title: "Periodic Reviews",
      description: "Access rights are reviewed periodically. Access is revoked immediately when it is no longer required or upon personnel role changes.",
      icon: FileCheck
    },
    {
      title: "Zero Shared Accounts",
      description: "Shared accounts are technically and procedurally prohibited. Every action must be attributable to a unique, verified identity.",
      icon: UserMinus
    },
    {
      title: "Traceability",
      description: "All system access is monitored and logged. Audit trails ensure full accountability and traceability for every administrative action.",
      icon: Monitor
    }
  ];

  return (
    <div className="min-h-screen bg-background pt-32 pb-20">
      <div className="container mx-auto px-4 md:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto space-y-12"
        >
          {/* Header */}
          <div className="space-y-4 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/5 border border-accent/10 text-accent text-[10px] font-bold uppercase tracking-[0.3em]">
              Compliance & Governance
            </div>
            <h1 className="text-4xl md:text-6xl font-display font-bold tracking-tight">
              Access Control <span className="text-accent">Policy</span>
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Defining the protocols and principles that secure Agrandintl systems and client environments.
            </p>
          </div>

          {/* Statement Card */}
          <div className="glass-card p-8 md:p-12 rounded-[2.5rem] border-white/5 bg-white/[0.01] relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-accent/20" />
            <div className="space-y-6 relative z-10">
              <div className="flex items-center gap-3 text-accent">
                <ShieldCheck className="w-6 h-6" />
                <h2 className="text-2xl font-display font-bold">Policy Overview</h2>
              </div>
              <div className="space-y-4 text-muted-foreground leading-relaxed text-base">
                <p>
                  Access to Agrandintl systems and client data is managed with extreme precision. We operate under the core assumption of mistrust, requiring explicit authorization for every point of entry. 
                </p>
                <p className="font-medium text-foreground/90 italic">
                  "All access is monitored and logged to ensure accountability and traceability."
                </p>
              </div>
            </div>
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {accessPrinciples.map((principle, idx) => (
              <motion.div
                key={principle.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="glass-card p-8 rounded-3xl border-white/5 bg-white/[0.01] hover:border-accent/20 transition-all group"
              >
                <div className="w-12 h-12 rounded-2xl bg-white/5 flex items-center justify-center text-accent mb-6 group-hover:scale-110 transition-transform">
                  <principle.icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-display font-bold mb-3">{principle.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {principle.description}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Footer Info */}
          <div className="pt-12 border-t border-white/5 text-center space-y-6">
            <p className="text-muted-foreground text-sm max-w-xl mx-auto">
              This Access Control Policy is an integral part of our broader Information Security Program and is reviewed on a quarterly basis.
            </p>
            <div className="pt-8 text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground/60">
              Document Version 2.2 | Last Updated: {lastUpdated}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
